var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Anchor, Button, FlexGrid, Stacker, Styler, TextField, TypographyBody, usePrevious } from '@honeycomb/ui-core';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalContext } from '@honeycomb/data';
import { FilterAccordion } from './FilterAccordion';
import { useCurrencySymbol, useFormatCurrency } from '../../utils';
const clampValues = (min, max, a, b) => {
    const clampedLower = Math.max(min, Math.min(a, b));
    const clampedUpper = Math.min(max, Math.max(a, b));
    return [clampedLower, clampedUpper];
};
export function BudgetFilter(_a) {
    var { id, heading, subHeading, min, max, lower: lowerProp, upper: upperProp, onChange } = _a, other = __rest(_a, ["id", "heading", "subHeading", "min", "max", "lower", "upper", "onChange"]);
    const { resourceStrings: { HC_MIN, HC_MAX, HC_RANGE, BUDGET_FILTER_RESET_CAPTION, BUDGET_FILTER_APPLY_CAPTION }, currentContext: { currencyCode }, } = useGlobalContext();
    const currencySymbol = useCurrencySymbol();
    // if we have the currency symbol of 1 char then we show that in the field with price else we show it in the label.
    const showSymbolInField = currencySymbol.length === 1;
    const valueFormatStyle = showSymbolInField ? 'currency' : 'decimal';
    // If we are not including the currency symbol in the field value, then we will inlcude it in the label instead.
    const minFieldLabel = showSymbolInField ? HC_MIN : `${HC_MIN} (${currencyCode})`;
    const maxFieldLabel = showSymbolInField ? HC_MAX : `${HC_MAX} (${currencyCode})`;
    const [lower, setLower] = useState(lowerProp);
    const [upper, setUpper] = useState(upperProp);
    const prevLowerProp = usePrevious(lowerProp);
    const prevUpperProp = usePrevious(upperProp);
    // Format each value
    const minFormatted = useFormatCurrency({ amount: min });
    const maxFormatted = useFormatCurrency({ amount: max });
    const lowerFormatted = useFormatCurrency({
        amount: lower,
        options: { style: valueFormatStyle },
    });
    const upperFormatted = useFormatCurrency({ amount: upper, options: { style: valueFormatStyle } });
    // Are the cuurent  values valid?
    const isValid = useMemo(() => {
        const lowerValid = lower >= min && lower <= max && lower <= upper;
        const upperValid = upper <= max && upper >= min && upper >= lower;
        return lowerValid && upperValid;
    }, [lower, upper, min, max]);
    const isApplyEnabled = useMemo(() => (lower !== lowerProp || upper !== upperProp) && isValid, [lower, upper, lowerProp, upperProp, isValid]);
    const isResetEnabled = useMemo(() => lower !== min || upper !== max, [lower, upper, min, max]);
    // If the lower or upper props change then update the local state.
    useEffect(() => {
        if (lowerProp !== prevLowerProp) {
            setLower(lowerProp);
        }
        if (upperProp !== prevUpperProp) {
            setUpper(upperProp);
        }
    }, [lowerProp, upperProp, prevLowerProp, prevUpperProp]);
    const handleChange = (event) => {
        const { name, value } = event.target;
        const num = parseInt(value.replace(/\D/g, '')) || 0;
        if (name === 'min') {
            setLower(num);
        }
        else {
            setUpper(num);
        }
    };
    const handleBlur = (event) => {
        const { name } = event.target;
        const isInvalidLower = lower < min || lower > max || lower > upper;
        const isInvalidUpper = upper > max || upper < min || upper < lower;
        if (name === 'min' && isInvalidLower) {
            setLower(min);
        }
        if (name === 'max' && isInvalidUpper) {
            setUpper(max);
        }
    };
    const handleApply = () => {
        if (!isApplyEnabled) {
            // Skip if values haven’t changed
            return;
        }
        const [clampedLower, clampedUpper] = clampValues(min, max, lower, upper);
        setLower(clampedLower);
        setUpper(clampedUpper);
        onChange(clampedLower, clampedUpper, id);
    };
    const handleReset = () => {
        setLower(min);
        setUpper(max);
        onChange(min, max, id);
    };
    return (React.createElement(FilterAccordion, Object.assign({ id: "BudgetFilter", heading: React.createElement(Styler, null,
            React.createElement("span", null, heading),
            subHeading && (React.createElement(TypographyBody, { component: "span", size: "xs" }, ` (${subHeading})`))) }, other),
        React.createElement(FlexGrid, { container: true, alignItems: "center", flexWrap: "nowrap", spacing: 2 },
            React.createElement(FlexGrid, { xs: "fill" },
                React.createElement(TextField, { name: "min", variant: "filled", size: "l", onChange: handleChange, onBlur: handleBlur, value: lower > 0 ? lowerFormatted : '', label: minFieldLabel, fullWidth: true })),
            React.createElement(FlexGrid, { xs: "auto", justifyContent: "center" }, "-"),
            React.createElement(FlexGrid, { xs: "fill" },
                React.createElement(TextField, { name: "max", variant: "filled", size: "l", onChange: handleChange, onBlur: handleBlur, value: upper > 0 ? upperFormatted : '', label: maxFieldLabel, fullWidth: true }))),
        React.createElement(Stacker, { direction: "row", mt: 2, spacing: 1, flexWrap: "wrap" },
            React.createElement(TypographyBody, { size: "s", color: "grey.tint.500" },
                HC_RANGE,
                ":"),
            React.createElement(TypographyBody, { size: "s", color: "grey.tint.500", weight: "semiBold" },
                minFormatted,
                " - ",
                maxFormatted)),
        React.createElement(FlexGrid, { container: true, alignItems: "center", justifyContent: "space-between", mt: 3 },
            React.createElement(FlexGrid, { xs: 6 },
                React.createElement(Anchor, { size: "s", weight: "semiBold", component: "button", color: "text.link", onClick: handleReset, disabled: !isResetEnabled }, BUDGET_FILTER_RESET_CAPTION)),
            React.createElement(FlexGrid, { xs: 6 },
                React.createElement(Button, { onClick: handleApply, color: "primary", variant: "outlined", size: "s", fullWidth: true, disabled: !isApplyEnabled }, BUDGET_FILTER_APPLY_CAPTION)))));
}
